import type { ChangeEvent } from "react"
import { useRef, useState } from "react"

import { clsx } from "clsx"

import {
  batteryConcertErrorCode as concertErrorCode,
  batteryConcertErrorMessage as concertErrorMessage,
  CONCERT_DOCUMENT_PENDING_MESSAGE,
} from "~/api/battery-concert-application-errors"
import type {
  BatteryConcertApplicationDto,
  BatteryConcertApplicationStatusDto,
  BatteryConcertStipulationDto,
} from "~/api/dto/battery-concert-application.dto"
import {
  CONCERT_DOCUMENT_ACCEPT,
  ConcertDocumentValidationError,
} from "~/api/hooks/concert-document-upload"

import {
  concertActivityLabel,
  concertApplicationOutcome,
  GENERATE_LOAN_DOCUMENTS_BUSY_LABEL,
  GENERATE_LOAN_DOCUMENTS_LABEL,
  LOAN_DOCUMENT_STATUS_COPY,
  LOAN_DOCUMENTS_HEADING,
  LOAN_DOCUMENTS_PREPARING_DETAIL,
  LOAN_DOCUMENTS_SENT_EITHER_WAY_DETAIL,
  SEND_LOAN_DOCUMENTS_BUSY_LABEL,
  SEND_LOAN_DOCUMENTS_BY_EMAIL_LABEL,
  SEND_LOAN_DOCUMENTS_LABEL,
  SIGN_LOAN_DOCUMENTS_IN_PERSON_BUSY_LABEL,
  SIGN_LOAN_DOCUMENTS_IN_PERSON_LABEL,
  CONCERT_SUPPORT_GUIDANCE,
  concertDateLabel,
  concertTermsRows,
  isResolvedStipulation,
  OUTCOME_TONE_CLASS,
  REPLACE_DOCUMENT_LABEL,
  RETRY_APPLICATION_LABEL,
  STIPULATION_PILL_CLASS,
  STIPULATION_STATUS_LABEL,
  SUBMIT_DOCUMENTS_FOR_REVIEW_LABEL,
  UPLOAD_DOCUMENT_LABEL,
} from "./concert-application-copy"
import type { ConcertApplicationBundle } from "./concert-kind-bundle"
import {
  ConcertSection,
  concertPrimaryButtonClass,
  concertSecondaryButtonClass,
  concertSectionClass,
} from "./concert-section"
import { APPLICATION_ACTIVITY_HEADING } from "../../../BatteryOnlyProposal/financing-status/application-status-labels"

/** `${prefix}-concert-${suffix}`: Battery's ids stay `battery-concert-…`. */
const concertTestId = (
  bundle: Pick<ConcertApplicationBundle, `testIdPrefix`>,
  suffix: string,
): string => `${bundle.testIdPrefix}-concert-${suffix}`

const sectionClass = concertSectionClass
const secondaryButtonClass = concertSecondaryButtonClass
const primaryButtonClass = concertPrimaryButtonClass
const Section = ConcertSection

/**
 * One card for what Concert decided and who applied: the badge and provider
 * reference, what the outcome means, and the applicants behind it.
 */
function Summary({
  bundle,
  application,
  canRetry,
  onRetry,
}: {
  bundle: ConcertApplicationBundle
  application: BatteryConcertApplicationDto
  canRetry: boolean
  onRetry?: () => void
}): JSX.Element {
  const outcome = concertApplicationOutcome(application, bundle.copy.offerNoun)
  return (
    <section
      aria-label="Application"
      data-testid={concertTestId(bundle, `application-summary`)}
      className={sectionClass}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-bp-border px-4 py-3">
        <span
          data-testid={concertTestId(bundle, `status-badge`)}
          className={clsx(
            `inline-flex items-center rounded-full border px-2.5 py-0.5 text-[length:var(--step--2)] font-semibold`,
            OUTCOME_TONE_CLASS[outcome.tone],
          )}
        >
          {outcome.badge}
        </span>
        <span
          data-testid={concertTestId(bundle, `reference`)}
          className="text-[length:var(--step--2)] text-bp-text-muted tabular-nums"
        >
          Concert reference {application.providerReference}
        </span>
      </div>
      <div
        data-testid={concertTestId(bundle, `outcome`)}
        className="flex flex-col gap-3 px-4 py-4"
      >
        <div>
          <p
            data-testid={concertTestId(bundle, `outcome-title`)}
            className="text-[length:var(--step-0)] font-semibold text-bp-text"
          >
            {outcome.title}
          </p>
          <p className="mt-1 text-[length:var(--step--1)] text-bp-text-muted">
            {outcome.detail}
          </p>
          {outcome.supportNeeded ? (
            <p
              data-testid={concertTestId(bundle, `support-guidance`)}
              className="mt-2 text-[length:var(--step--1)] text-bp-gold"
            >
              {CONCERT_SUPPORT_GUIDANCE}
            </p>
          ) : null}
        </div>
        {canRetry && onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className={clsx(primaryButtonClass, `self-start`)}
          >
            {RETRY_APPLICATION_LABEL}
          </button>
        ) : null}
      </div>
    </section>
  )
}

function LoanTerms({
  bundle,
  application,
}: {
  bundle: ConcertApplicationBundle
  application: BatteryConcertApplicationDto
}): JSX.Element {
  const rows = concertTermsRows(application)
  const approved = application.approvedTerms != null
  const terms = application.approvedTerms ?? application.submittedTerms
  return (
    <Section
      title="Loan details"
      testId={concertTestId(bundle, `loan-terms`)}
      trailing={
        <span className="text-[length:var(--step--2)] text-bp-text-muted">
          Credit tier {terms.creditTier} · {terms.creditQuality}
        </span>
      }
    >
      <table className="w-full text-[length:var(--step--2)]">
        <thead>
          <tr className="text-left text-bp-text-dim">
            <th scope="col" className="px-4 py-2 font-normal">
              Term
            </th>
            <th scope="col" className="px-4 py-2 font-normal">
              {approved ? `Submitted` : `Submitted terms`}
            </th>
            {approved ? (
              <th scope="col" className="px-4 py-2 font-normal">
                Approved
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-bp-border">
          {rows.map((row) => (
            <tr
              key={row.key}
              data-testid={concertTestId(bundle, `term-${row.key}`)}
              data-changed={row.changed ? `true` : undefined}
            >
              <th
                scope="row"
                className="px-4 py-2 text-left font-medium text-bp-text"
              >
                {row.label}
              </th>
              <td
                className={clsx(
                  `px-4 py-2 tabular-nums`,
                  row.changed ? `text-bp-text-dim line-through` : `text-bp-text`,
                )}
              >
                {row.submitted}
              </td>
              {approved ? (
                <td
                  className={clsx(
                    `px-4 py-2 font-semibold tabular-nums`,
                    row.changed ? `text-bp-gold` : `text-bp-text`,
                  )}
                >
                  {row.approved}
                  {row.changed ? (
                    <span className="ml-2 rounded-full border border-bp-gold/40 px-1.5 py-0.5 text-[10px] font-bold tracking-widest uppercase">
                      Changed
                    </span>
                  ) : null}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  )
}

function StipulationRow({
  bundle,
  proposalId,
  stipulation,
  canUpload,
}: {
  bundle: ConcertApplicationBundle
  proposalId: string
  stipulation: BatteryConcertStipulationDto
  canUpload: boolean
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null)
  const upload = bundle.useUpload()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const resolved = isResolvedStipulation(stipulation.status)
  const hasUpload = stipulation.uploads.some(
    (entry) => entry.status === `uploaded`,
  )
  const showUpload =
    bundle.supportingDocumentActions &&
    canUpload &&
    !resolved &&
    stipulation.requirementId != null

  const onFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    event.target.value = ``
    if (!file || upload.isLoading || stipulation.requirementId == null) {
      return
    }
    setErrorMessage(null)
    upload.mutate(
      {
        proposalId,
        stipulationId: stipulation.id,
        requirementId: stipulation.requirementId,
        file,
      },
      {
        onError: (error) => {
          setErrorMessage(
            error instanceof ConcertDocumentValidationError
              ? error.message
              : concertErrorCode(error) === `CONCERT_DOCUMENT_PENDING`
                ? CONCERT_DOCUMENT_PENDING_MESSAGE
                : (concertErrorMessage(error) ?? `Upload failed. Try again.`),
          )
        },
      },
    )
  }

  return (
    <li
      data-testid={concertTestId(bundle, `stipulation`)}
      data-status={stipulation.status}
      className="flex flex-col gap-2 px-4 py-3"
    >
      {/* The requirement and its status read as one line; the instructions run
          full width beneath so a long block never pushes the status away. */}
      <div className="flex items-baseline justify-between gap-3">
        <p className="min-w-0 text-[length:var(--step--1)] font-semibold text-bp-text">
          {stipulation.title ?? `Supporting document`}
          {stipulation.required ? null : (
            <span className="ml-2 font-normal text-bp-text-dim">Optional</span>
          )}
        </p>
        <span
          className={clsx(
            `inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-[length:var(--step--2)] font-semibold`,
            STIPULATION_PILL_CLASS[stipulation.status],
          )}
        >
          {STIPULATION_STATUS_LABEL[stipulation.status]}
        </span>
      </div>
      {stipulation.instructions ? (
        // Plain text only: the server strips markup and links, and this
        // screen never offers an outbound Concert destination.
        <p
          data-testid={concertTestId(bundle, `stipulation-instructions`)}
          className="text-[length:var(--step--2)] text-bp-text-muted"
        >
          {stipulation.instructions}
        </p>
      ) : null}
      {stipulation.uploads.length > 0 ? (
        <ul className="flex flex-col gap-1 text-[length:var(--step--2)]">
          {stipulation.uploads.map((entry) => (
            <li
              key={entry.id}
              data-testid={concertTestId(bundle, `upload`)}
              className="flex items-baseline justify-between gap-3"
            >
              <span className="truncate text-bp-text">{entry.filename}</span>
              <span
                className={clsx(
                  `shrink-0`,
                  entry.status === `failed`
                    ? `text-bp-danger`
                    : entry.status === `pending`
                      ? `text-bp-gold`
                      : `text-bp-text-muted`,
                )}
              >
                {entry.status === `uploaded`
                  ? `Uploaded ${concertDateLabel(entry.createdAt)}`
                  : entry.status === `pending`
                    ? `Awaiting confirmation`
                    : `Upload failed`}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {showUpload ? (
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={inputRef}
            type="file"
            accept={CONCERT_DOCUMENT_ACCEPT}
            className="sr-only"
            aria-label={`${hasUpload ? REPLACE_DOCUMENT_LABEL : UPLOAD_DOCUMENT_LABEL}: ${stipulation.title ?? `Supporting document`}`}
            data-testid={concertTestId(bundle, `upload-input`)}
            disabled={upload.isLoading}
            onChange={onFileChange}
          />
          <button
            type="button"
            disabled={upload.isLoading}
            onClick={() => inputRef.current?.click()}
            className={secondaryButtonClass}
          >
            {upload.isLoading
              ? `Uploading`
              : hasUpload
                ? REPLACE_DOCUMENT_LABEL
                : UPLOAD_DOCUMENT_LABEL}
          </button>
          {errorMessage ? (
            <p
              role="alert"
              className="text-[length:var(--step--2)] text-bp-danger"
            >
              {errorMessage}
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

function Stipulations({
  bundle,
  proposalId,
  application,
  canUpload,
}: {
  bundle: ConcertApplicationBundle
  proposalId: string
  application: BatteryConcertApplicationDto
  canUpload: boolean
}): JSX.Element {
  const submitDocuments = bundle.useSubmitDocuments()
  const { stipulations } = application
  const unresolved = stipulations.filter(
    (stipulation) => !isResolvedStipulation(stipulation.status),
  ).length
  const readyToSubmit = stipulations.some(
    (stipulation) =>
      (stipulation.status === `open` || stipulation.status === `rejected`) &&
      stipulation.uploads.some((entry) => entry.status === `uploaded`),
  )

  return (
    <Section
      title="Stipulations"
      testId={concertTestId(bundle, `stipulations`)}
      trailing={
        stipulations.length > 0 ? (
          <span
            data-testid={concertTestId(bundle, `unresolved-count`)}
            className="text-[length:var(--step--2)] text-bp-text-muted"
          >
            {unresolved > 0 ? `${unresolved} unresolved` : `All resolved`}
          </span>
        ) : null
      }
    >
      {stipulations.length === 0 ? (
        <p className="px-4 py-3 text-[length:var(--step--2)] text-bp-text-muted">
          No Concert stipulations are listed for this application.
        </p>
      ) : (
        <ul className="divide-y divide-bp-border">
          {stipulations.map((stipulation) => (
            <StipulationRow
              key={stipulation.id}
              bundle={bundle}
              proposalId={proposalId}
              stipulation={stipulation}
              canUpload={canUpload}
            />
          ))}
        </ul>
      )}
      {bundle.supportingDocumentActions &&
      canUpload &&
      stipulations.length > 0 ? (
        <div className="flex flex-col items-start gap-2 border-t border-bp-border px-4 py-3">
          {submitDocuments.isError ? (
            <p
              role="alert"
              className="text-[length:var(--step--2)] text-bp-danger"
            >
              {concertErrorMessage(submitDocuments.error) ??
                `Unable to submit documents for review. Try again.`}
            </p>
          ) : null}
          <button
            type="button"
            disabled={!readyToSubmit || submitDocuments.isLoading}
            onClick={() => submitDocuments.mutate({ proposalId })}
            className={primaryButtonClass}
          >
            {submitDocuments.isLoading
              ? `Submitting documents`
              : SUBMIT_DOCUMENTS_FOR_REVIEW_LABEL}
          </button>
        </div>
      ) : null}
    </Section>
  )
}

function LoanDocuments({
  bundle,
  proposalId,
  application,
  action,
  sendAction,
}: {
  bundle: ConcertApplicationBundle
  proposalId: string
  application: BatteryConcertApplicationDto
  action: BatteryConcertApplicationStatusDto[`actions`][`generateLoanDocuments`]
  sendAction: BatteryConcertApplicationStatusDto[`actions`][`sendLoanDocuments`]
}): JSX.Element {
  const generate = bundle.useGenerateLoanDocuments()
  const send = bundle.useSendLoanDocuments()
  const { status, readyToSend, generatedAt, sentAt, signedAt } =
    application.loanDocuments
  const copy = LOAN_DOCUMENT_STATUS_COPY[status]
  // Concert accepted the request but has not finished building the package.
  const preparing = status === `generated` && !readyToSend
  const pending = generate.isLoading || send.isLoading

  return (
    <Section
      title={LOAN_DOCUMENTS_HEADING}
      testId={concertTestId(bundle, `loan-documents`)}
      trailing={
        <span
          data-testid={concertTestId(bundle, `loan-documents-status`)}
          className={clsx(
            `inline-flex items-center rounded-full border px-2.5 py-0.5 text-[length:var(--step--2)] font-semibold`,
            OUTCOME_TONE_CLASS[copy.tone],
          )}
        >
          {copy.label}
        </span>
      }
    >
      <div className="flex flex-col items-start gap-3 px-4 py-4">
        <p className="text-[length:var(--step--1)] text-bp-text-muted">
          {preparing
            ? LOAN_DOCUMENTS_PREPARING_DETAIL
            : status === `sent` && bundle.inPersonLoanDocuments
              ? LOAN_DOCUMENTS_SENT_EITHER_WAY_DETAIL
              : copy.detail}
          {generatedAt != null
            ? ` Generated ${concertDateLabel(generatedAt)}.`
            : ``}
          {sentAt != null ? ` Sent ${concertDateLabel(sentAt)}.` : ``}
          {signedAt != null ? ` Signed ${concertDateLabel(signedAt)}.` : ``}
        </p>
        {generate.isError || send.isError ? (
          <p
            role="alert"
            className="text-[length:var(--step--2)] text-bp-danger"
          >
            {concertErrorMessage(generate.error ?? send.error) ??
              `Unable to complete that request. Try again.`}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-3">
          {action.allowed ? (
            <button
              type="button"
              disabled={pending}
              onClick={() => generate.mutate({ proposalId })}
              className={primaryButtonClass}
            >
              {generate.isLoading
                ? GENERATE_LOAN_DOCUMENTS_BUSY_LABEL
                : GENERATE_LOAN_DOCUMENTS_LABEL}
            </button>
          ) : null}
          {sendAction.allowed && bundle.inPersonLoanDocuments ? (
            <>
              <button
                type="button"
                disabled={pending}
                onClick={() => send.mutate({ proposalId, method: `email` })}
                className={primaryButtonClass}
              >
                {send.isLoading && send.variables?.method === `email`
                  ? SEND_LOAN_DOCUMENTS_BUSY_LABEL
                  : SEND_LOAN_DOCUMENTS_BY_EMAIL_LABEL}
              </button>
              <button
                type="button"
                disabled={pending}
                onClick={() => send.mutate({ proposalId, method: `in_person` })}
                className={secondaryButtonClass}
              >
                {send.isLoading && send.variables?.method === `in_person`
                  ? SIGN_LOAN_DOCUMENTS_IN_PERSON_BUSY_LABEL
                  : SIGN_LOAN_DOCUMENTS_IN_PERSON_LABEL}
              </button>
            </>
          ) : sendAction.allowed ? (
            <button
              type="button"
              disabled={pending}
              onClick={() => send.mutate({ proposalId })}
              className={primaryButtonClass}
            >
              {send.isLoading
                ? SEND_LOAN_DOCUMENTS_BUSY_LABEL
                : SEND_LOAN_DOCUMENTS_LABEL}
            </button>
          ) : null}
        </div>
        {!action.allowed && !sendAction.allowed && sendAction.reason != null ? (
          <p
            data-testid={concertTestId(bundle, `loan-documents-reason`)}
            className="text-[length:var(--step--2)] text-bp-text-muted"
          >
            {sendAction.reason}
          </p>
        ) : null}
      </div>
    </Section>
  )
}

function Activity({
  bundle,
  application,
}: {
  bundle: ConcertApplicationBundle
  application: BatteryConcertApplicationDto
}): JSX.Element {
  return (
    <Section
      title={APPLICATION_ACTIVITY_HEADING}
      testId={concertTestId(bundle, `activity`)}
    >
      {application.activity.length === 0 ? (
        <p className="px-4 py-3 text-[length:var(--step--2)] text-bp-text-muted">
          No activity recorded yet.
        </p>
      ) : (
        <ul className="divide-y divide-bp-border">
          {application.activity.map((event) => (
            <li
              key={event.id}
              data-testid={concertTestId(bundle, `activity-event`)}
              className="flex items-baseline justify-between gap-3 px-4 py-3 text-[length:var(--step--2)]"
            >
              <span className="text-bp-text">{concertActivityLabel(event)}</span>
              <span className="shrink-0 text-bp-text-muted">
                {concertDateLabel(event.occurredAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

function NoApplication({
  bundle,
  status,
}: {
  bundle: ConcertApplicationBundle
  status: BatteryConcertApplicationStatusDto
}): JSX.Element {
  return (
    <div
      data-testid={concertTestId(bundle, `no-application`)}
      className="flex flex-col gap-2 text-[length:var(--step--1)] text-bp-text-muted"
    >
      <p>{bundle.copy.noApplication}</p>
      {status.setupStatus === `failed` ? (
        <p role="alert" className="text-bp-danger">
          {bundle.copy.setupFailed}
        </p>
      ) : status.setupStatus === `unconfirmed` ? (
        <p role="alert" className="text-bp-gold">
          {bundle.copy.setupUnconfirmed}
        </p>
      ) : null}
    </div>
  )
}

/**
 * A Concert application's status for any proposal kind's drawers. Opening it
 * never creates a customer or submits credit; the server refreshes a
 * dispatched application on every mount. Every control shows only while the
 * server's action allows it; Change Orders are never rendered.
 */
export function ConcertApplicationStatus({
  bundle,
  proposalId,
  onRetry,
}: {
  bundle: ConcertApplicationBundle
  proposalId: string
  /** Opens the application form again for a confirmed provider rejection. */
  onRetry?: () => void
}): JSX.Element {
  const query = bundle.useStatus(proposalId, {
    refetchOnMount: `always`,
  })
  const { Supplemental } = bundle
  const status = query.data

  if (status == null) {
    return (
      <div
        role={query.isError ? `alert` : `status`}
        data-testid={concertTestId(bundle, `status-loading`)}
        className="flex-1 text-[length:var(--step--1)] text-bp-text-muted"
      >
        {query.isError
          ? `Concert application status could not be loaded. Close and try again.`
          : `Loading Concert application status…`}
      </div>
    )
  }

  const { application, actions } = status
  if (application == null) {
    return <NoApplication bundle={bundle} status={status} />
  }

  return (
    <div
      data-testid={concertTestId(bundle, `application-status`)}
      className="flex flex-1 flex-col gap-4"
    >
      <Summary
        bundle={bundle}
        application={application}
        canRetry={actions.submit.allowed && application.submission === `failed`}
        onRetry={onRetry}
      />
      <LoanTerms bundle={bundle} application={application} />
      <LoanDocuments
        bundle={bundle}
        proposalId={proposalId}
        application={application}
        action={actions.generateLoanDocuments}
        sendAction={actions.sendLoanDocuments}
      />
      {/* Its own section, below Concert's: two envelopes, two signatures. */}
      {Supplemental ? <Supplemental proposalId={proposalId} /> : null}
      <Stipulations
        bundle={bundle}
        proposalId={proposalId}
        application={application}
        canUpload={actions.supportingDocuments.allowed}
      />
      <Activity bundle={bundle} application={application} />
    </div>
  )
}
